import { useTranslation } from "react-i18next"

type Translations = typeof defaultTranslations
type ProjectDashboardTranslationKey = Exclude<
  keyof Translations,
  "noData" | "notStarted" | "inProgress" | "completed" | "rejected"
>
type UseTranslationsParams = {
  totalNumberOfTasks?: number
  numberOfCompletedTasks?: number
  numberOfNotCompletedTasks?: number
  numberOfTasks?: number
  rejectedDate?: string
  numberOfDaysUntilPlannedStart?: number
  numberOfDaysFromPlannedStart?: number
  numberOfDaysProjectStartedLate?: number
  numberOfDaysUntilPlannedCompletion?: number
  numberOfDaysFromPlannedCompletion?: number
  numberOfDaysProjectCompletedLate?: number
}

const defaultTranslations = {
  customerCardHeader: "Customer",
  currentStatusCardHeader: "Current Status",
  latestStatusDescriptionCardHeader: "Latest Status Description",
  projectManagerCardHeader: "Project manager",
  projectParticipantsCardHeader: "Project participants",
  taskResourcesCardHeader: "Task resources",
  tasksSectionHeader: "Tasks",
  resourceParticipationCardHeader: "Resource participation in tasks",
  noData: "No data",
  pieChartCardHeader: "{{count}} tasks in total",
  completedBarCardHeader: "{{count}} completed tasks",
  notCompletedBarCardHeader: "{{count}} not completed tasks",
  pieChartCardHeaderForOneTask: "1 task in total",
  completedBardCardHeaderForOneTask: "1 completed task",
  notCompletedBarCardHeaderForOneTask: "1 not completed task",

  notStarted: "Not started",
  inProgress: "In progress",
  completed: "Completed",
  rejected: "Rejected",
  onTimeLabel: "On time",
  lateLabel: "Late",
  pastStartLabel: "Past start",
  noScheduleLabel: "No schedule",
  numberOfTasksLabel: "{{count}} tasks",
  oneTaskLabel: "1 task",

  scheduleCardHeader: "Schedule",
  startDate: "Start date",
  endDate: "End date",
  startedOn: "Started on",
  completedOn: "Completed on",
  scheduleCompletionLabel: "Schedule completion",
  manuallyUpdatedCompletionLabel: "Manually updated completion",
  taskBasedCompletionLabel: "Task-based completion",

  missingPlannedStartDate: "Project is missing a planned start date",
  shouldHaveStartedDaysAgo: "Project should have started {{count}} days ago",
  shouldHaveStartedWeeksAgo: "Project should have started {{count}} weeks ago",
  shouldHaveStartedOneDayAgo: "Project should have started 1 day ago",
  shouldHaveStartedOneWeekAgo: "Project should have started 1 week ago",
  projectStartedOnTime: "Project started on time",
  startedDaysLate: "Project started {{count}} days late",
  startedWeeksLate: "Project started {{count}} weeks late",
  startedOneDayLate: "Project started 1 day late",
  startedOneWeekLate: "Project started 1 week late",
  willStartInDays: "Project will start in {{count}} days",
  willStartInWeeks: "Project will start in {{count}} weeks",
  willStartInOneDay: "Project will start in 1 day",
  willStartInOneWeek: "Project will start in 1 week",

  missingPlannedEndDate: "Project is missing a planned end date",
  shouldHaveCompletedDaysAgo: "Project should have been completed {{count}} days ago",
  shouldHaveCompletedWeeksAgo: "Project should have been completed {{count}} weeks ago",
  shouldHaveCompletedOneDayAgo: "Project should have been completed 1 day ago",
  shouldHaveCompletedOneWeekAgo: "Project should have been completed 1 week ago",
  completedOnTime: "Project completed on time",
  completedDaysLate: "Project completed {{count}} days late",
  completedWeeksLate: "Project completed {{count}} weeks late",
  completedOneDayLate: "Project completed 1 day late",
  completedOneWeekLate: "Project completed 1 week late",
  shouldCompleteInDays: "Project should be completed in {{count}} days",
  shouldCompleteInWeeks: "Project should be completed in {{count}} weeks",
  shouldCompleteInOneDay: "Project should be completed in 1 day",
  shouldCompleteInOneWeek: "Project should be completed in 1 week",

  rejectedComment: "This project was rejected on {{date}}",
}

const getDaysOrWeeks = (numberOfDays: number): { value: number; unit: "day" | "week" } => {
  const shouldDisplayWeeks = numberOfDays > 28
  if (shouldDisplayWeeks) {
    const numberOfWeeks = Math.floor(numberOfDays / 7)
    return { value: numberOfWeeks, unit: "week" }
  }
  return { value: numberOfDays, unit: "day" }
}

const tProjectDashboardDuration = ({
  numberOfDays,
  oneDayKey,
  daysKey,
  oneWeekKey,
  weeksKey,
}: {
  numberOfDays?: number
  oneDayKey: ProjectDashboardTranslationKey
  daysKey: ProjectDashboardTranslationKey
  oneWeekKey: ProjectDashboardTranslationKey
  weeksKey: ProjectDashboardTranslationKey
}) => {
  if (numberOfDays === undefined) {
    return {
      key: `projectDashboard.${daysKey}` as const,
      defaultValue: defaultTranslations[daysKey],
      count: 0,
    }
  }

  const { value, unit } = getDaysOrWeeks(numberOfDays)
  const key = unit === "day" ? (value === 1 ? oneDayKey : daysKey) : value === 1 ? oneWeekKey : weeksKey

  return {
    key: `projectDashboard.${key}` as const,
    defaultValue: defaultTranslations[key],
    count: value,
  }
}

export const useTranslations = ({
  totalNumberOfTasks,
  numberOfCompletedTasks,
  numberOfNotCompletedTasks,
  numberOfTasks,
  rejectedDate,
  numberOfDaysUntilPlannedStart,
  numberOfDaysFromPlannedStart,
  numberOfDaysProjectStartedLate,
  numberOfDaysUntilPlannedCompletion,
  numberOfDaysFromPlannedCompletion,
  numberOfDaysProjectCompletedLate,
}: UseTranslationsParams = {}): Translations => {
  const { t: tProject } = useTranslation("project")
  const { t: tTreegrid } = useTranslation("treegrid")
  const shouldHaveStartedDaysAgo = tProjectDashboardDuration({
    numberOfDays: numberOfDaysFromPlannedStart,
    oneDayKey: "shouldHaveStartedOneDayAgo",
    daysKey: "shouldHaveStartedDaysAgo",
    oneWeekKey: "shouldHaveStartedOneWeekAgo",
    weeksKey: "shouldHaveStartedWeeksAgo",
  })
  const startedDaysLate = tProjectDashboardDuration({
    numberOfDays: numberOfDaysProjectStartedLate,
    oneDayKey: "startedOneDayLate",
    daysKey: "startedDaysLate",
    oneWeekKey: "startedOneWeekLate",
    weeksKey: "startedWeeksLate",
  })
  const willStartInDays = tProjectDashboardDuration({
    numberOfDays: numberOfDaysUntilPlannedStart,
    oneDayKey: "willStartInOneDay",
    daysKey: "willStartInDays",
    oneWeekKey: "willStartInOneWeek",
    weeksKey: "willStartInWeeks",
  })
  const shouldHaveCompletedDaysAgo = tProjectDashboardDuration({
    numberOfDays: numberOfDaysFromPlannedCompletion,
    oneDayKey: "shouldHaveCompletedOneDayAgo",
    daysKey: "shouldHaveCompletedDaysAgo",
    oneWeekKey: "shouldHaveCompletedOneWeekAgo",
    weeksKey: "shouldHaveCompletedWeeksAgo",
  })
  const completedDaysLate = tProjectDashboardDuration({
    numberOfDays: numberOfDaysProjectCompletedLate,
    oneDayKey: "completedOneDayLate",
    daysKey: "completedDaysLate",
    oneWeekKey: "completedOneWeekLate",
    weeksKey: "completedWeeksLate",
  })
  const shouldCompleteInDays = tProjectDashboardDuration({
    numberOfDays: numberOfDaysUntilPlannedCompletion,
    oneDayKey: "shouldCompleteInOneDay",
    daysKey: "shouldCompleteInDays",
    oneWeekKey: "shouldCompleteInOneWeek",
    weeksKey: "shouldCompleteInWeeks",
  })

  return {
    customerCardHeader: tProject("projectDashboard.customerCardHeader", {
      defaultValue: defaultTranslations.customerCardHeader,
    }),
    currentStatusCardHeader: tProject("projectDashboard.currentStatusCardHeader", {
      defaultValue: defaultTranslations.currentStatusCardHeader,
    }),
    latestStatusDescriptionCardHeader: tProject("projectDashboard.latestStatusDescriptionCardHeader", {
      defaultValue: defaultTranslations.latestStatusDescriptionCardHeader,
    }),
    projectManagerCardHeader: tProject("projectDashboard.projectManagerCardHeader", {
      defaultValue: defaultTranslations.projectManagerCardHeader,
    }),
    projectParticipantsCardHeader: tProject("projectDashboard.projectParticipantsCardHeader", {
      defaultValue: defaultTranslations.projectParticipantsCardHeader,
    }),
    taskResourcesCardHeader: tProject("projectDashboard.taskResourcesCardHeader", {
      defaultValue: defaultTranslations.taskResourcesCardHeader,
    }),
    tasksSectionHeader: tProject("projectDashboard.tasksSectionHeader", {
      defaultValue: defaultTranslations.tasksSectionHeader,
    }),
    resourceParticipationCardHeader: tProject("projectDashboard.resourceParticipationCardHeader", {
      defaultValue: defaultTranslations.resourceParticipationCardHeader,
    }),
    noData: tTreegrid("noData", { defaultValue: defaultTranslations.noData }),
    pieChartCardHeader: tProject("projectDashboard.pieChartCardHeader", {
      defaultValue: defaultTranslations.pieChartCardHeader,
      count: totalNumberOfTasks ?? 0,
    }),
    completedBarCardHeader: tProject("projectDashboard.completedBarCardHeader", {
      defaultValue: defaultTranslations.completedBarCardHeader,
      count: numberOfCompletedTasks ?? 0,
    }),
    notCompletedBarCardHeader: tProject("projectDashboard.notCompletedBarCardHeader", {
      defaultValue: defaultTranslations.notCompletedBarCardHeader,
      count: numberOfNotCompletedTasks ?? 0,
    }),
    notStarted: tProject("projectStatusMenu.notStarted", { defaultValue: defaultTranslations.notStarted }),
    inProgress: tProject("projectStatusMenu.inProgress", { defaultValue: defaultTranslations.inProgress }),
    completed: tProject("projectStatusMenu.completed", { defaultValue: defaultTranslations.completed }),
    rejected: tProject("projectStatusMenu.rejected", { defaultValue: defaultTranslations.rejected }),
    onTimeLabel: tProject("projectDashboard.onTimeLabel", { defaultValue: defaultTranslations.onTimeLabel }),
    lateLabel: tProject("projectDashboard.lateLabel", { defaultValue: defaultTranslations.lateLabel }),
    pastStartLabel: tProject("projectDashboard.pastStartLabel", {
      defaultValue: defaultTranslations.pastStartLabel,
    }),
    noScheduleLabel: tProject("projectDashboard.noScheduleLabel", {
      defaultValue: defaultTranslations.noScheduleLabel,
    }),
    numberOfTasksLabel:
      numberOfTasks === 1
        ? tProject("projectDashboard.oneTaskLabel", { defaultValue: defaultTranslations.oneTaskLabel })
        : tProject("projectDashboard.numberOfTasksLabel", {
            defaultValue: defaultTranslations.numberOfTasksLabel,
            count: numberOfTasks ?? 0,
          }),
    oneTaskLabel: tProject("projectDashboard.oneTaskLabel", { defaultValue: defaultTranslations.oneTaskLabel }),
    scheduleCardHeader: tProject("projectDashboard.scheduleCardHeader", {
      defaultValue: defaultTranslations.scheduleCardHeader,
    }),
    startDate: tProject("projectDashboard.startDate", { defaultValue: defaultTranslations.startDate }),
    endDate: tProject("projectDashboard.endDate", { defaultValue: defaultTranslations.endDate }),
    startedOn: tProject("projectDashboard.startedOn", { defaultValue: defaultTranslations.startedOn }),
    completedOn: tProject("projectDashboard.completedOn", { defaultValue: defaultTranslations.completedOn }),
    scheduleCompletionLabel: tProject("projectDashboard.scheduleCompletionLabel", {
      defaultValue: defaultTranslations.scheduleCompletionLabel,
    }),
    manuallyUpdatedCompletionLabel: tProject("projectDashboard.manuallyUpdatedCompletionLabel", {
      defaultValue: defaultTranslations.manuallyUpdatedCompletionLabel,
    }),
    taskBasedCompletionLabel: tProject("projectDashboard.taskBasedCompletionLabel", {
      defaultValue: defaultTranslations.taskBasedCompletionLabel,
    }),
    missingPlannedStartDate: tProject("projectDashboard.missingPlannedStartDate", {
      defaultValue: defaultTranslations.missingPlannedStartDate,
    }),
    shouldHaveStartedDaysAgo: tProject(shouldHaveStartedDaysAgo.key, {
      defaultValue: shouldHaveStartedDaysAgo.defaultValue,
      count: shouldHaveStartedDaysAgo.count,
    }),
    shouldHaveStartedWeeksAgo: tProject("projectDashboard.shouldHaveStartedWeeksAgo", {
      defaultValue: defaultTranslations.shouldHaveStartedWeeksAgo,
    }),
    projectStartedOnTime: tProject("projectDashboard.projectStartedOnTime", {
      defaultValue: defaultTranslations.projectStartedOnTime,
    }),
    startedDaysLate: tProject(startedDaysLate.key, {
      defaultValue: startedDaysLate.defaultValue,
      count: startedDaysLate.count,
    }),
    startedWeeksLate: tProject("projectDashboard.startedWeeksLate", {
      defaultValue: defaultTranslations.startedWeeksLate,
    }),
    willStartInDays: tProject(willStartInDays.key, {
      defaultValue: willStartInDays.defaultValue,
      count: willStartInDays.count,
    }),
    willStartInWeeks: tProject("projectDashboard.willStartInWeeks", {
      defaultValue: defaultTranslations.willStartInWeeks,
    }),
    missingPlannedEndDate: tProject("projectDashboard.missingPlannedEndDate", {
      defaultValue: defaultTranslations.missingPlannedEndDate,
    }),
    shouldHaveCompletedDaysAgo: tProject(shouldHaveCompletedDaysAgo.key, {
      defaultValue: shouldHaveCompletedDaysAgo.defaultValue,
      count: shouldHaveCompletedDaysAgo.count,
    }),
    shouldHaveCompletedWeeksAgo: tProject("projectDashboard.shouldHaveCompletedWeeksAgo", {
      defaultValue: defaultTranslations.shouldHaveCompletedWeeksAgo,
    }),
    completedOnTime: tProject("projectDashboard.completedOnTime", {
      defaultValue: defaultTranslations.completedOnTime,
    }),
    completedDaysLate: tProject(completedDaysLate.key, {
      defaultValue: completedDaysLate.defaultValue,
      count: completedDaysLate.count,
    }),
    completedWeeksLate: tProject("projectDashboard.completedWeeksLate", {
      defaultValue: defaultTranslations.completedWeeksLate,
    }),
    shouldCompleteInDays: tProject(shouldCompleteInDays.key, {
      defaultValue: shouldCompleteInDays.defaultValue,
      count: shouldCompleteInDays.count,
    }),
    shouldCompleteInWeeks: tProject("projectDashboard.shouldCompleteInWeeks", {
      defaultValue: defaultTranslations.shouldCompleteInWeeks,
    }),
    rejectedComment: tProject("projectDashboard.rejectedComment", {
      defaultValue: defaultTranslations.rejectedComment,
      date: rejectedDate ?? "{{date}}",
    }),
    shouldHaveStartedOneDayAgo: tProject("projectDashboard.shouldHaveStartedOneDayAgo", {
      defaultValue: defaultTranslations.shouldHaveStartedOneDayAgo,
    }),
    shouldHaveStartedOneWeekAgo: tProject("projectDashboard.shouldHaveStartedOneWeekAgo", {
      defaultValue: defaultTranslations.shouldHaveStartedOneWeekAgo,
    }),
    startedOneDayLate: tProject("projectDashboard.startedOneDayLate", {
      defaultValue: defaultTranslations.startedOneDayLate,
    }),
    startedOneWeekLate: tProject("projectDashboard.startedOneWeekLate", {
      defaultValue: defaultTranslations.startedOneWeekLate,
    }),
    willStartInOneDay: tProject("projectDashboard.willStartInOneDay", {
      defaultValue: defaultTranslations.willStartInOneDay,
    }),
    willStartInOneWeek: tProject("projectDashboard.willStartInOneWeek", {
      defaultValue: defaultTranslations.willStartInOneWeek,
    }),
    shouldHaveCompletedOneDayAgo: tProject("projectDashboard.shouldHaveCompletedOneDayAgo", {
      defaultValue: defaultTranslations.shouldHaveCompletedOneDayAgo,
    }),
    shouldHaveCompletedOneWeekAgo: tProject("projectDashboard.shouldHaveCompletedOneWeekAgo", {
      defaultValue: defaultTranslations.shouldHaveCompletedOneWeekAgo,
    }),
    completedOneDayLate: tProject("projectDashboard.completedOneDayLate", {
      defaultValue: defaultTranslations.completedOneDayLate,
    }),
    completedOneWeekLate: tProject("projectDashboard.completedOneWeekLate", {
      defaultValue: defaultTranslations.completedOneWeekLate,
    }),
    shouldCompleteInOneDay: tProject("projectDashboard.shouldCompleteInOneDay", {
      defaultValue: defaultTranslations.shouldCompleteInOneDay,
    }),
    shouldCompleteInOneWeek: tProject("projectDashboard.shouldCompleteInOneWeek", {
      defaultValue: defaultTranslations.shouldCompleteInOneWeek,
    }),
    pieChartCardHeaderForOneTask: tProject("projectDashboard.pieChartCardHeaderForOneTask", {
      defaultValue: defaultTranslations.pieChartCardHeaderForOneTask,
    }),
    completedBardCardHeaderForOneTask: tProject("projectDashboard.completedBardCardHeaderForOneTask", {
      defaultValue: defaultTranslations.completedBardCardHeaderForOneTask,
    }),
    notCompletedBarCardHeaderForOneTask: tProject("projectDashboard.notCompletedBarCardHeaderForOneTask", {
      defaultValue: defaultTranslations.notCompletedBarCardHeaderForOneTask,
    }),
  }
}
